import * as PIXI from 'pixi.js';
import { Scene } from '../core/Scene';
import type { Game } from '../core/Game';
import { SKINS, getUnlockedSkins, unlockSkin, equipSkin, getEquippedSkin } from '../content/Skins';
import { ENCYCLOPEDIA_CARDS, getUnlockedCards } from '../content/Encyclopedia';

type Tab = 'shop' | 'skins' | 'encyclopedia';

export class ShopScene extends Scene {
  private currentTab: Tab = 'shop';
  private tabContainer = new PIXI.Container();
  private contentContainer = new PIXI.Container();

  constructor(game: Game) {
    super(game);
  }

  enter(): void {
    const bounds = this.game.getScreenBounds();
    const scale = Math.min(bounds.width, bounds.height) / 600;

    // Background
    const bg = new PIXI.Graphics();
    bg.rect(0, 0, bounds.width, bounds.height);
    bg.fill(0x1a1a2e);
    this.container.addChild(bg);

    // Title
    const title = new PIXI.Text({
      text: 'МАГАЗИН',
      style: {
        fill: 0xffd700,
        fontSize: Math.round(36 * scale),
        fontFamily: 'Arial',
        fontWeight: 'bold',
      },
    });
    title.anchor.set(0.5);
    title.x = bounds.width / 2;
    title.y = bounds.top + 40 * scale;
    this.container.addChild(title);

    // Tab buttons
    const tabs: { id: Tab; label: string }[] = [
      { id: 'shop', label: 'Товары' },
      { id: 'skins', label: 'Скины' },
      { id: 'encyclopedia', label: 'Энциклопедия' },
    ];

    const tabW = Math.round(120 * scale);
    const tabSpacing = Math.round(130 * scale);
    const tabStartX = bounds.width / 2 - ((tabs.length - 1) * tabSpacing) / 2;

    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i]!;
      const tabBtn = new PIXI.Container();
      const tabBg = new PIXI.Graphics();
      tabBg.roundRect(-tabW / 2, -18 * scale, tabW, 36 * scale, 8);
      tabBg.fill(this.currentTab === tab.id ? 0x4CAF50 : 0x333355);
      tabBtn.addChild(tabBg);
      const tabText = new PIXI.Text({
        text: tab.label,
        style: { fill: 0xffffff, fontSize: Math.round(12 * scale), fontFamily: 'Arial' },
      });
      tabText.anchor.set(0.5);
      tabBtn.addChild(tabText);
      tabBtn.x = tabStartX + i * tabSpacing;
      tabBtn.y = bounds.top + 85 * scale;
      tabBtn.eventMode = 'static';
      tabBtn.cursor = 'pointer';
      tabBtn.on('pointerdown', () => {
        this.currentTab = tab.id;
        this.refresh(bounds, scale);
      });
      this.container.addChild(tabBtn);
    }

    // Back button
    const backBtn = new PIXI.Container();
    const backBg = new PIXI.Graphics();
    backBg.roundRect(0, 0, 80 * scale, 36 * scale, 8);
    backBg.fill(0x444444);
    backBtn.addChild(backBg);
    const backText = new PIXI.Text({
      text: '← Назад',
      style: { fill: 0xffffff, fontSize: Math.round(12 * scale), fontFamily: 'Arial' },
    });
    backText.anchor.set(0.5);
    backText.x = 40 * scale;
    backText.y = 18 * scale;
    backBtn.addChild(backText);
    backBtn.x = 20 * scale;
    backBtn.y = bounds.top + 20 * scale;
    backBtn.eventMode = 'static';
    backBtn.cursor = 'pointer';
    backBtn.on('pointerdown', () => {
      this.game.switchScene('menu');
    });
    this.container.addChild(backBtn);

    // Content area
    this.container.addChild(this.tabContainer);
    this.container.addChild(this.contentContainer);

    this.refresh(bounds, scale);
  }

  private refresh(bounds: { width: number; height: number; top: number }, scale: number): void {
    this.tabContainer.removeChildren();
    this.contentContainer.removeChildren();

    switch (this.currentTab) {
      case 'shop':
        this.renderShop(bounds, scale);
        break;
      case 'skins':
        this.renderSkins(bounds, scale);
        break;
      case 'encyclopedia':
        this.renderEncyclopedia(bounds, scale);
        break;
    }
  }

  private renderShop(bounds: { width: number; height: number; top: number }, scale: number): void {
    const items = [
      { id: 'lives_3', name: '+3 жизни', price: 50, desc: '3 дополнительные жизни' },
      { id: 'lives_5', name: '+5 жизней', price: 80, desc: '5 дополнительных жизней' },
      { id: 'boost_ammo', name: '+2 патрона', price: 30, desc: 'Доп. патроны на 3 уровня' },
      { id: 'boost_speed', name: 'Скорость ×1.5', price: 40, desc: 'Буст скорости на 3 уровня' },
      { id: 'boost_shield', name: 'Щит 10 сек', price: 35, desc: 'Защита от врагов' },
    ];

    let yOffset = bounds.top + 130 * scale;
    const itemWidth = bounds.width - 60 * scale;
    const itemHeight = 70 * scale;

    for (const item of items) {
      const itemContainer = new PIXI.Container();

      const itemBg = new PIXI.Graphics();
      itemBg.roundRect(0, 0, itemWidth, itemHeight, 12);
      itemBg.fill(0x2a2a4a);
      itemBg.stroke({ color: 0x444466, width: 2 });
      itemContainer.addChild(itemBg);

      const nameText = new PIXI.Text({
        text: item.name,
        style: { fill: 0xffffff, fontSize: Math.round(16 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
      });
      nameText.x = 16 * scale;
      nameText.y = 12 * scale;
      itemContainer.addChild(nameText);

      const descText = new PIXI.Text({
        text: item.desc,
        style: { fill: 0x888888, fontSize: Math.round(11 * scale), fontFamily: 'Arial' },
      });
      descText.x = 16 * scale;
      descText.y = 40 * scale;
      itemContainer.addChild(descText);

      const priceBtn = new PIXI.Container();
      const priceBg = new PIXI.Graphics();
      priceBg.roundRect(0, 0, 90 * scale, 32 * scale, 8);
      priceBg.fill(0xffaa00);
      priceBtn.addChild(priceBg);
      const priceText = new PIXI.Text({
        text: `⭐ ${item.price}`,
        style: { fill: 0x000000, fontSize: Math.round(13 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
      });
      priceText.anchor.set(0.5);
      priceText.x = 45 * scale;
      priceText.y = 16 * scale;
      priceBtn.addChild(priceText);
      priceBtn.x = itemWidth - 110 * scale;
      priceBtn.y = (itemHeight - 32 * scale) / 2;
      priceBtn.eventMode = 'static';
      priceBtn.cursor = 'pointer';
      priceBtn.on('pointerdown', () => this.showFeedback(`✓ ${item.name} куплено!`, bounds, scale));
      itemContainer.addChild(priceBtn);

      itemContainer.x = 30 * scale;
      itemContainer.y = yOffset;
      this.contentContainer.addChild(itemContainer);
      yOffset += itemHeight + 12 * scale;
    }
  }

  private renderSkins(bounds: { width: number; height: number; top: number }, scale: number): void {
    const unlocked = getUnlockedSkins();
    const equipped = getEquippedSkin();
    let yOffset = bounds.top + 130 * scale;
    const itemWidth = bounds.width - 60 * scale;
    const itemHeight = 70 * scale;

    for (const skin of SKINS) {
      const isUnlocked = unlocked.includes(skin.id);
      const isEquipped = equipped === skin.id;

      const itemContainer = new PIXI.Container();

      const itemBg = new PIXI.Graphics();
      itemBg.roundRect(0, 0, itemWidth, itemHeight, 12);
      itemBg.fill(isEquipped ? 0x2a4a2a : 0x2a2a4a);
      itemBg.stroke({ color: isEquipped ? 0x44ff44 : 0x444466, width: 2 });
      itemContainer.addChild(itemBg);

      // Skin preview circle
      const preview = new PIXI.Graphics();
      preview.circle(24 * scale, itemHeight / 2, 18 * scale);
      preview.fill(skin.bodyColor);
      if (skin.accessory) {
        preview.circle(24 * scale, itemHeight / 2 - 14 * scale, 6 * scale);
        preview.fill(skin.accessoryColor ?? 0xffffff);
      }
      itemContainer.addChild(preview);

      const nameText = new PIXI.Text({
        text: skin.name,
        style: { fill: 0xffffff, fontSize: Math.round(16 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
      });
      nameText.x = 56 * scale;
      nameText.y = 15 * scale;
      itemContainer.addChild(nameText);

      const actionBtn = new PIXI.Container();
      const actionBg = new PIXI.Graphics();

      if (isEquipped) {
        actionBg.roundRect(0, 0, 90 * scale, 32 * scale, 8);
        actionBg.fill(0x44ff44);
        actionBtn.addChild(actionBg);
        const actionText = new PIXI.Text({
          text: 'Надет',
          style: { fill: 0x000000, fontSize: Math.round(12 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
        });
        actionText.anchor.set(0.5);
        actionText.x = 45 * scale;
        actionText.y = 16 * scale;
        actionBtn.addChild(actionText);
      } else if (isUnlocked) {
        actionBg.roundRect(0, 0, 90 * scale, 32 * scale, 8);
        actionBg.fill(0x2196F3);
        actionBtn.addChild(actionBg);
        const actionText = new PIXI.Text({
          text: 'Надеть',
          style: { fill: 0xffffff, fontSize: Math.round(12 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
        });
        actionText.anchor.set(0.5);
        actionText.x = 45 * scale;
        actionText.y = 16 * scale;
        actionBtn.addChild(actionText);
        actionBtn.on('pointerdown', () => {
          equipSkin(skin.id);
          this.refresh(bounds, scale);
        });
      } else {
        actionBg.roundRect(0, 0, 90 * scale, 32 * scale, 8);
        actionBg.fill(0xffaa00);
        actionBtn.addChild(actionBg);
        const actionText = new PIXI.Text({
          text: `⭐ ${skin.price}`,
          style: { fill: 0x000000, fontSize: Math.round(12 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
        });
        actionText.anchor.set(0.5);
        actionText.x = 45 * scale;
        actionText.y = 16 * scale;
        actionBtn.addChild(actionText);
        actionBtn.on('pointerdown', () => {
          unlockSkin(skin.id);
          equipSkin(skin.id);
          this.refresh(bounds, scale);
        });
      }

      actionBtn.x = itemWidth - 110 * scale;
      actionBtn.y = (itemHeight - 32 * scale) / 2;
      actionBtn.eventMode = 'static';
      actionBtn.cursor = 'pointer';
      itemContainer.addChild(actionBtn);

      itemContainer.x = 30 * scale;
      itemContainer.y = yOffset;
      this.contentContainer.addChild(itemContainer);
      yOffset += itemHeight + 12 * scale;
    }
  }

  private renderEncyclopedia(bounds: { width: number; height: number; top: number }, scale: number): void {
    const unlocked = getUnlockedCards();
    let yOffset = bounds.top + 130 * scale;
    const itemWidth = bounds.width - 60 * scale;

    for (const card of ENCYCLOPEDIA_CARDS) {
      const isUnlocked = unlocked.includes(card.id);

      const cardContainer = new PIXI.Container();

      const cardBg = new PIXI.Graphics();
      cardBg.roundRect(0, 0, itemWidth, 100 * scale, 12);
      cardBg.fill(isUnlocked ? 0x2a2a4a : 0x1a1a2a);
      cardBg.stroke({ color: 0x444466, width: 2 });
      cardContainer.addChild(cardBg);

      const categoryText = new PIXI.Text({
        text: card.category,
        style: { fill: 0xffaa00, fontSize: Math.round(10 * scale), fontFamily: 'Arial' },
      });
      categoryText.x = 16 * scale;
      categoryText.y = 10 * scale;
      cardContainer.addChild(categoryText);

      const titleText = new PIXI.Text({
        text: isUnlocked ? card.title : '???',
        style: { fill: 0xffffff, fontSize: Math.round(16 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
      });
      titleText.x = 16 * scale;
      titleText.y = 28 * scale;
      cardContainer.addChild(titleText);

      if (isUnlocked) {
        const summaryText = new PIXI.Text({
          text: card.summary,
          style: { fill: 0xaaaaaa, fontSize: Math.round(11 * scale), fontFamily: 'Arial', wordWrap: true, wordWrapWidth: itemWidth - 32 * scale },
        });
        summaryText.x = 16 * scale;
        summaryText.y = 52 * scale;
        cardContainer.addChild(summaryText);
      }

      cardContainer.x = 30 * scale;
      cardContainer.y = yOffset;
      this.contentContainer.addChild(cardContainer);
      yOffset += 115 * scale;
    }
  }

  private showFeedback(text: string, bounds: { width: number; height: number }, scale: number): void {
    const msg = new PIXI.Text({
      text,
      style: { fill: 0x44ff44, fontSize: Math.round(20 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
    });
    msg.anchor.set(0.5);
    msg.x = bounds.width / 2;
    msg.y = bounds.height / 2;
    this.game.app.stage.addChild(msg);
    setTimeout(() => msg.destroy(), 2000);
  }

  exit(): void {
    this.container.removeChildren();
  }
}
